import {
  type CSVGenerator,
  generateAdvertisersCSV,
  generateCampaignsCSV,
  generateCampaignTypesCSV,
  generateShowsCSV,
  generateCampaignsCSVWithBreakdown,
  generateAdvertisersCSVWithBreakdown,
  generateCampaignTypesCSVWithBreakdown,
  generateShowsCSVWithBreakdown,
  type CSVGeneratorWithBreakdown,
} from './csvGenerators';
import { DataTypes } from '@/types';
import { CSV_CONFIG } from '@/config/csvConfig';
import { LevelOfDetail } from '@/types';

interface DataToCSVProps<T> {
  type: DataTypes;
  data: T;
  breakdown?: LevelOfDetail;
}

export class CSVGenerationError extends Error {
  constructor(
    message: string,
    public readonly type: DataTypes,
    public readonly errorCode: 'NO_DATA' | 'INVALID_TYPE' | 'GENERATION_FAILED'
  ) {
    super(message);
    this.name = 'CSVGenerationError';
  }
}

const validateCSVInput = <T>(type: DataTypes, data: T): void => {
  if (!data) {
    throw new CSVGenerationError(`No data provided for type: ${type}`, type, 'NO_DATA');
  }

  if (!Array.isArray(data) && type !== 'campaign_types') {
    throw new CSVGenerationError(`Data must be an array for type: ${type}`, type, 'INVALID_TYPE');
  }

  if (type === 'campaign_types' && typeof data !== 'object') {
    throw new CSVGenerationError(`Data must be an object for type: ${type}`, type, 'INVALID_TYPE');
  }
};

const csvGenerators = {
  advertisers: generateAdvertisersCSV,
  campaigns: generateCampaignsCSV,
  campaign_types: generateCampaignTypesCSV,
  shows: generateShowsCSV,
};

const csvGeneratorsWithBreakdown = {
  advertisers: generateAdvertisersCSVWithBreakdown,
  campaigns: generateCampaignsCSVWithBreakdown,
  campaign_types: generateCampaignTypesCSVWithBreakdown,
  shows: generateShowsCSVWithBreakdown,
};

function generateCsvData<T>({ type, data, breakdown }: DataToCSVProps<T>) {
  if (breakdown) {
    const generator = csvGeneratorsWithBreakdown[type] as CSVGeneratorWithBreakdown<T>;
    return generator(data, breakdown);
  }
  const generator = csvGenerators[type] as CSVGenerator<T>;
  return generator(data);
}

// Maps the API data into a CSV format
export const apiDataToCSV = <T>({ type, data, breakdown }: DataToCSVProps<T>) => {
  try {
    validateCSVInput(type, data);

    const { headers, rows } = generateCsvData<T>({ type, data, breakdown });

    const csvContent = [headers, ...rows].join(CSV_CONFIG.LINE_SEPARATOR);
    return new Blob([csvContent], { type: CSV_CONFIG.MIME_TYPE });
  } catch (error) {
    console.error('Error generating CSV', error);
    throw new CSVGenerationError('Failed to generate CSV', type, 'GENERATION_FAILED');
  }
};
